import { motion } from 'framer-motion';
import { ArrowRight, Code, MessageSquare, Search, Shield, Microscope } from 'lucide-react';
import BLOCKS from '../data/blocks';

const recipes = [
  {
    name: 'RAG Chatbot',
    description: 'Company knowledge assistant with retrieval-augmented generation',
    icon: MessageSquare,
    elements: [
      { symbol: 'Em', block: 'foundation' },
      { symbol: 'Vb', block: 'infrastructure' },
      { symbol: 'Rg', block: 'techniques' },
      { symbol: 'Pm', block: 'techniques' },
      { symbol: 'Lm', block: 'models' },
      { symbol: 'Gr', block: 'safety' },
    ],
    formula: 'Em + Vb + Rg + Pm + Lm + Gr',
  },
  {
    name: 'Agentic Coding Assistant',
    description: 'Autonomous code generation with tool use and planning',
    icon: Code,
    elements: [
      { symbol: 'Lm', block: 'models' },
      { symbol: 'Cd', block: 'models' },
      { symbol: 'Fc', block: 'techniques' },
      { symbol: 'Tu', block: 'techniques' },
      { symbol: 'Ag', block: 'techniques' },
      { symbol: 'Co', block: 'techniques' },
      { symbol: 'Me', block: 'techniques' },
    ],
    formula: 'Lm + Cd + Fc + Tu + Ag + Co + Me',
  },
  {
    name: 'Enterprise Document Intelligence',
    description: 'Multimodal document understanding with safety controls',
    icon: Search,
    elements: [
      { symbol: 'Mm', block: 'models' },
      { symbol: 'Em', block: 'foundation' },
      { symbol: 'Vb', block: 'infrastructure' },
      { symbol: 'Rg', block: 'techniques' },
      { symbol: 'Gr', block: 'safety' },
      { symbol: 'Ip', block: 'safety' },
      { symbol: 'Au', block: 'safety' },
    ],
    formula: 'Mm + Em + Vb + Rg + Gr + Ip + Au',
  },
  {
    name: 'AI Research System',
    description: 'Multi-agent research with planning and knowledge graphs',
    icon: Microscope,
    elements: [
      { symbol: 'Lm', block: 'models' },
      { symbol: 'Ma', block: 'techniques' },
      { symbol: 'Or', block: 'techniques' },
      { symbol: 'Tu', block: 'techniques' },
      { symbol: 'Me', block: 'techniques' },
      { symbol: 'Pl', block: 'techniques' },
      { symbol: 'Re', block: 'techniques' },
      { symbol: 'Kg', block: 'data' },
    ],
    formula: 'Lm + Ma + Or + Tu + Me + Pl + Re + Kg',
  },
  {
    name: 'Aligned Frontier Model',
    description: 'Production-safe model with comprehensive alignment',
    icon: Shield,
    elements: [
      { symbol: 'Tf', block: 'architecture' },
      { symbol: 'Pt', block: 'training' },
      { symbol: 'Ft', block: 'training' },
      { symbol: 'Rh', block: 'training' },
      { symbol: 'Ca', block: 'safety' },
      { symbol: 'Rt', block: 'safety' },
      { symbol: 'Ip', block: 'safety' },
      { symbol: 'Gr', block: 'safety' },
      { symbol: 'Al', block: 'safety' },
    ],
    formula: 'Tf + Pt + Ft + Rh + Ca + Rt + Ip + Gr + Al',
  },
];

export default function SystemRecipes() {
  return (
    <div className="max-w-6xl mx-auto px-4">
      {/* Section Header */}
      <motion.div
        className="text-center mb-12"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
      >
        <h2 className="text-3xl md:text-4xl font-display font-bold mb-4">
          System <span className="text-gradient">Recipes</span>
        </h2>
        <p className="text-gray-400 max-w-2xl mx-auto">
          Real AI systems are combinations of elements. Here are some common "molecular formulas"
          showing how elements combine into production systems.
        </p>
      </motion.div>

      {/* Training Pipeline Image */}
      <motion.div
        className="mb-16"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
      >
        <h3 className="text-xl font-bold mb-4 text-center">The Training Pipeline</h3>
        <img
          src="/images/Training_Pipeline_Diagram.png"
          alt="Modern AI Training Pipeline"
          className="w-full max-w-2xl mx-auto rounded-xl border border-white/10"
        />
      </motion.div>

      {/* Data Pipeline Image */}
      <motion.div
        className="mb-16"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
      >
        <h3 className="text-xl font-bold mb-4 text-center">The Data Pipeline</h3>
        <img
          src="/images/Data_Pipeline_Flowchart.png"
          alt="Modern AI Data Pipeline"
          className="w-full max-w-4xl mx-auto rounded-xl border border-white/10"
        />
      </motion.div>

      {/* Recipes Grid */}
      <div className="space-y-6">
        {recipes.map((recipe, index) => {
          const Icon = recipe.icon;
          return (
            <motion.div
              key={recipe.name}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="p-6 rounded-xl bg-dark-800/50 border border-white/5 hover:border-white/10 transition-colors"
            >
              <div className="flex flex-col md:flex-row md:items-center gap-6">
                {/* Recipe Info */}
                <div className="flex items-center gap-4 md:w-1/3">
                  <div className="w-12 h-12 rounded-xl bg-cyber-blue/20 flex items-center justify-center">
                    <Icon className="w-6 h-6 text-cyber-blue" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg">{recipe.name}</h3>
                    <p className="text-sm text-gray-400">{recipe.description}</p>
                  </div>
                </div>

                {/* Elements */}
                <div className="flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    {recipe.elements.map((el, i) => {
                      const block = BLOCKS[el.block];
                      return (
                        <div key={i} className="flex items-center gap-1">
                          <div
                            className="w-10 h-10 rounded-lg flex items-center justify-center text-xs font-bold border"
                            style={{
                              backgroundColor: `${block.color}20`,
                              borderColor: `${block.color}50`,
                              color: block.color,
                            }}
                          >
                            {el.symbol}
                          </div>
                          {i < recipe.elements.length - 1 && (
                            <span className="text-gray-600 mx-1">+</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Formula */}
                <div className="md:w-auto">
                  <code className="text-xs text-gray-500 bg-dark-700 px-3 py-2 rounded-lg block md:inline">
                    {recipe.formula}
                  </code>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Usage Tips */}
      <motion.div
        className="mt-12 p-6 rounded-xl bg-cyber-blue/10 border border-cyber-blue/20"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
      >
        <h3 className="text-lg font-bold text-cyber-blue mb-4">How to Use Recipes</h3>
        <div className="grid md:grid-cols-3 gap-6 text-sm">
          <div>
            <h4 className="font-semibold text-white mb-2">1. Evaluate Claims</h4>
            <p className="text-gray-400">
              When someone says "we built an AI system," ask which elements are present.
            </p>
          </div>
          <div>
            <h4 className="font-semibold text-white mb-2">2. Identify Gaps</h4>
            <p className="text-gray-400">
              Missing elements often indicate risks or limitations in a system.
            </p>
          </div>
          <div>
            <h4 className="font-semibold text-white mb-2">3. Plan Architecture</h4>
            <p className="text-gray-400">
              Start with a recipe and customize for your specific needs.
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
